export type Utm = {
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
  term?: string;
  gclid?: string;
  referrer?: string;
  ts?: string; // ISO timestamp
};

function isUtm(value: unknown): value is Utm {
  if (value === null || typeof value !== 'object') {
    return false;
  }

  return Object.values(value).every(
    v => v === undefined || typeof v === 'string',
  );
}

function parseQuery(search: string): Partial<Utm> {
  const p = new URLSearchParams(search);
  const pick = (k: string) => p.get(k) ?? undefined;
  const u: Partial<Utm> = {
    source: pick('utm_source'),
    medium: pick('utm_medium'),
    campaign: pick('utm_campaign'),
    content: pick('utm_content'),
    term: pick('utm_term'),
    gclid: pick('gclid'),
  };
  return u;
}

const firstKey = 'utm_first';
const lastKey = 'utm_last';

export function initUtmCapture(): void {
  if (globalThis.window === undefined) {
    return;
  }

  const q = parseQuery(globalThis.location.search);
  const hasUtm = Object.values(q).some(Boolean);
  if (!hasUtm) {
    return;
  }

  const base: Utm = {
    ...q,
    referrer: document.referrer || undefined,
    ts: new Date().toISOString(),
  };

  if (!localStorage.getItem(firstKey)) {
    localStorage.setItem(firstKey, JSON.stringify(base));
  }

  sessionStorage.setItem(lastKey, JSON.stringify(base));
}

export function getUtm(): {first?: Utm; last?: Utm} {
  try {
    const first = localStorage.getItem(firstKey);
    const last = sessionStorage.getItem(lastKey);
    return {
      first: first
        ? (() => {
            const parsed: unknown = JSON.parse(first);
            return isUtm(parsed) ? parsed : undefined;
          })()
        : undefined,
      last: last
        ? (() => {
            const parsed: unknown = JSON.parse(last);
            return isUtm(parsed) ? parsed : undefined;
          })()
        : undefined,
    };
  } catch {
    return {};
  }
}

export function getUtmFirstHeaders(): Record<string, string> {
  const {first} = getUtm();
  if (!first) {
    return {};
  }

  const h: Record<string, string> = {};
  if (first.source) {
    h['X-UTM-Source'] = first.source;
  }

  if (first.medium) {
    h['X-UTM-Medium'] = first.medium;
  }

  if (first.campaign) {
    h['X-UTM-Campaign'] = first.campaign;
  }

  if (first.content) {
    h['X-UTM-Content'] = first.content;
  }

  if (first.term) {
    h['X-UTM-Term'] = first.term;
  }

  if (first.gclid) {
    h['X-GCLID'] = first.gclid;
  }

  return h;
}
